import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../hooks/queries';
import { useSettings } from '../hooks/queries';
import { LayoutDashboard, MapPin, Users, Settings, LogOut, Menu, X, Phone, FileText, Bell, Check, ArrowLeft, ClipboardList, Vote } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { hasFullAccess } from '../lib/permissions';
import { api } from '../lib/api';
import { UserAvatar } from './UserAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification Bell Component
const NotificationBell = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.user_id;
  const { data, refetch } = useNotifications(userId);
  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unread_count ?? 0;
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const buttonRef = React.useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    }
  }, [isOpen]);

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`${API}/notifications/${notificationId}/read`, {});
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      setLoading(true);
      await api.put(`${API}/notifications/read-all`, {});
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch (error) {
      console.error('Failed to mark all as read');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) refetch(); }}
        className="relative p-2 rounded-lg hover:bg-secondary transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5 text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div
            className="fixed w-80 bg-white rounded-lg shadow-lg border border-border z-50 overflow-hidden"
            style={{ top: dropdownPosition.top, right: dropdownPosition.left }}
            data-testid="notification-dropdown"
          >
            <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/30">
              <h3 className="font-semibold text-sm">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  disabled={loading}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <Check className="h-3 w-3" />
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-[min(24rem,70vh)] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  No notifications
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.notification_id}
                    className={cn(
                      'px-4 py-3 border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer',
                      !notification.is_read && 'bg-blue-50/50'
                    )}
                    onClick={() => !notification.is_read && markAsRead(notification.notification_id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn('mt-0.5 px-2 py-0.5 rounded text-xs font-medium', getSeverityColor(notification.severity))}>
                        {notification.severity}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{notification.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatTime(notification.created_at)}</p>
                      </div>
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const defaultAppName = process.env.REACT_APP_APP_NAME || 'RajjePulse';
  const { data: settings } = useSettings();
  const appName = settings?.app_name || defaultAppName;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Check if we should show back button (not on main nav pages)
  const mainNavPaths = ['/dashboard', '/constituencies', '/call-centre', '/audit-log', '/users', '/settings', '/mp-requests', '/voting-boxes'];
  const showBackButton = !mainNavPaths.includes(location.pathname);

  const handleBack = () => {
    // Smart back navigation based on current path
    const path = location.pathname;
    if (path.startsWith('/records')) {
      const params = new URLSearchParams(location.search);
      const constituencyId = params.get('constituency_id');
      navigate(`/campaigns?constituency_id=${constituencyId}`);
    } else if (path.startsWith('/campaigns')) {
      navigate('/constituencies');
    } else if (path.startsWith('/fiyavalhu')) {
      const params = new URLSearchParams(location.search);
      const constituencyId = params.get('constituency_id');
      navigate(`/campaigns?constituency_id=${constituencyId}`);
    } else if (path.startsWith('/turnout')) {
      navigate(-1); // Browser back for public pages
    } else {
      navigate(-1); // Default browser back
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'nav.dashboard' },
    { name: 'Constituencies', href: '/constituencies', icon: MapPin, permission: 'nav.constituencies' },
    { name: 'Voting Boxes', href: '/voting-boxes', icon: Vote, permission: 'nav.voting_boxes' },
    { name: 'MP Requests', href: '/mp-requests', icon: ClipboardList, permission: 'nav.mp_requests' },
    { name: 'Call Centre', href: '/call-centre', icon: Phone, permission: 'nav.call_centre' },
    { name: 'Audit Log', href: '/audit-log', icon: FileText, permission: 'nav.audit_log' },
    { name: 'Users', href: '/users', icon: Users, permission: 'nav.users' },
    { name: 'Settings', href: '/settings', icon: Settings, permission: 'nav.settings' },
  ];

  const filteredNavigation = navigation.filter(item => {
    if (!user) return false;
    if (hasFullAccess(user)) return true;
    const ep = user.effective_permissions ?? [];
    return ep.includes(item.permission);
  });

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-surface">
      {/* Desktop Sidebar */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow border-r border-border bg-white overflow-y-auto">
          <div className="flex items-center justify-between flex-shrink-0 px-6 py-6 border-b border-border">
            <h1 className="text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>{appName}</h1>
            {user && <NotificationBell />}
          </div>
          <div className="flex-1 flex flex-col justify-between">
            <nav className="flex-1 px-3 py-4 space-y-1">
              {filteredNavigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <button
                    key={item.name}
                    onClick={() => navigate(item.href)}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                    className={cn(
                      'w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-secondary hover:text-foreground'
                    )}
                  >
                    <Icon className={cn('mr-3 h-5 w-5 flex-shrink-0')} />
                    {item.name}
                  </button>
                );
              })}
            </nav>
            <div className="px-3 pb-4 border-t border-border pt-4">
              <div className="flex items-center px-3 py-2 mb-2">
                <UserAvatar user={user} size="sm" />
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate capitalize">{user?.role_name || user?.role_slug?.replace(/_/g, ' ') || user?.role?.replace(/_/g, ' ')}</p>
                </div>
              </div>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start"
                data-testid="logout-button"
              >
                <LogOut className="mr-3 h-5 w-5" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            {showBackButton && (
              <button
                onClick={handleBack}
                className="p-2 -ml-2 rounded-md text-foreground hover:bg-secondary"
                data-testid="mobile-back-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <h1 className="text-xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>{appName}</h1>
          </div>
          <div className="flex items-center gap-2">
            {user && <NotificationBell />}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-md text-foreground hover:bg-secondary"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <nav className="px-2 pb-3 space-y-1 bg-white border-b border-border">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setMobileMenuOpen(false);
                  }}
                  className={cn(
                    'w-full group flex items-center px-3 py-2.5 text-base font-medium rounded-md',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-secondary'
                  )}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </button>
              );
            })}
            <button
              onClick={handleLogout}
              className="w-full group flex items-center px-3 py-2.5 text-base font-medium rounded-md text-foreground hover:bg-secondary"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </button>
          </nav>
        )}
      </div>

      {/* Main Content */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Desktop Back Button Bar - shown when on sub-pages */}
        {showBackButton && (
          <div className="hidden md:block sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-border">
            <div className="px-8 py-3">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="desktop-back-button"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            </div>
          </div>
        )}
        <main className="flex-1 pt-16 md:pt-0">
          {children}
        </main>
      </div>
    </div>
  );
};